import { useState, useEffect, useCallback } from "react";
import { useModal } from "@/hooks";
import { useQueryParam, StringParam } from "use-query-params";
import Form from "react-bootstrap/Form";
import { callApi } from "@/lib/apiClient/api";

const searchDebounceTimeout = 250; // ms
export const DEFAULT_PAGINATION_LIMIT = 20;

const getValue = ({ isInitialValue, showValue, value, isMulti }) => {
  if (isInitialValue && showValue) {
    return isMulti ? showValue : { label: showValue, value: showValue };
  }
  return value;
};

const CustomSelect = ({
  children,
  fieldApiParams,
  items,
  input,
  showValue,
  mapResponse,
  isMulti = false,
  setFieldValue,
  isAutoCompleteFilter,
  hasCreateButton,
  createButtonOptions,
  isDisabled,
  values
}) => {
  const [isInitialValue, setIsInitialValue] = useState(true);
  const [value, setValue] = useState("");
  const [inputValue, onInputChangeRaw] = useState("");
  const [showValueFromQuery] = useQueryParam(`${input.name}Label`, StringParam);

  const openModal = useModal();
  const onInputChange = useCallback((nextInputValue, { action }) => {
    if (action === "input-change") {
      onInputChangeRaw(nextInputValue);
    }
  }, []);

  if (!showValue && isInitialValue && isAutoCompleteFilter) {
    // if is AutoCompleteFilter in nested SearchBox value is in initialValues otherwize get it from query
    showValue = values[`${input.name}Label`] || showValueFromQuery;
  }

  const sortMultiValues = useCallback(
    (dragIndex, hoverIndex) => {
      setValue((value) => {
        const newArray = isInitialValue ? [...showValue] : [...value];
        const item = isInitialValue ? showValue[dragIndex] : value[dragIndex];
        newArray.splice(dragIndex, 1);
        newArray.splice(hoverIndex, 0, item);
        newArray.map((item, index) => (item.order = index + 1));
        if (isInitialValue) {
          setIsInitialValue(false);
        }
        input.onChange(newArray.map((e) => e.value));
        return newArray;
      });
    },
    [isInitialValue, showValue, input]
  );

  useEffect(() => {
    if (input.value === "") {
      setValue("");
    }
  }, [input.value]);

  const onChange = (chosenItem) => {
    setIsInitialValue(false);
    const data = chosenItem || "";
    setValue(data);

    let chosenValue;
    if (isMulti) {
      chosenValue = chosenItem ? chosenItem.map((item) => item.value) : [];
    } else {
      chosenValue = chosenItem ? chosenItem.value : "";
    }
    if (isAutoCompleteFilter) {
      // put the label in searchForm params (and from there in the url query) to be able to get it on refresh
      setFieldValue(`${input.name}Label`, chosenItem?.label);
    }
    input.onChange(chosenValue);
  };

  const mapToSelectObj = (res) => {
    if (mapResponse) {
      return res["hydra:member"].map(mapResponse);
    }

    if (fieldApiParams) {
      return res.map((val) => ({
        label: val.name,
        value: val.id
      }));
    }
    return Object.keys(res).map((key) => ({
      label: res[key].label,
      value: res[key].value
    }));
  };

  const fetchOptions = async (searchParams, page) => {
    const { resource, apiParams, searchParamName } = fieldApiParams;
    let apiCallParams = { limit: DEFAULT_PAGINATION_LIMIT, page, ...apiParams };
    if (searchParams && fieldApiParams) {
      apiCallParams = {
        [searchParamName]: searchParams,
        limit: DEFAULT_PAGINATION_LIMIT,
        page,
        ...apiParams
      };
    }
    const response = await callApi({
      url: "/" + resource,
      params: apiCallParams
    });
    return mapToSelectObj(response.data);
  };

  const loadOptions = async (search, prevOptions, { page }) => {
    const options = fieldApiParams
      ? await fetchOptions(search, page)
      : await Promise.resolve(mapToSelectObj(items));

    let filteredOptions;
    if (!search) {
      filteredOptions = options;
    } else {
      const searchLower = search.toLowerCase();

      filteredOptions = options.filter(({ label }) =>
        label.toLowerCase().includes(searchLower)
      );
    }

    const hasMore = fieldApiParams
      ? options.length === DEFAULT_PAGINATION_LIMIT
      : filteredOptions.length > prevOptions.length + DEFAULT_PAGINATION_LIMIT;
    const slicedOptions = fieldApiParams
      ? filteredOptions
      : filteredOptions.slice(prevOptions.length, prevOptions.length + 50);

    return {
      options: slicedOptions,
      hasMore,
      additional: {
        page: page + 1
      }
    };
  };

  return (
    <div className="d-flex flex-column">
      <div className="w-100">
        {children({
          onChange,
          fieldApiParams,
          loadOptions,
          debounceTimeout: searchDebounceTimeout,
          value: getValue({ isInitialValue, showValue, value, isMulti }),
          onInputChange,
          inputValue,
          sortMultiValues
        })}
        {hasCreateButton && !isDisabled && (
          <div className="position-relative">
            <Form.Text className="text-endavo-primary-darker position-absolute end-0">
              <button
                className="btn-semantic"
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  openModal({
                    isOpen: true,
                    entity:
                      createButtonOptions?.modal || fieldApiParams?.resource,
                    params: {
                      isNestedForm: true,
                      value: getValue({
                        isInitialValue,
                        showValue,
                        value,
                        isMulti
                      }),
                      onChange,
                      isMulti
                    }
                  });
                }}
              >
                {createButtonOptions?.buttonText || "Create a New One"}
              </button>
            </Form.Text>
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomSelect;
